import api, { APIError } from "api";
import { supportApiBase } from "api/constants";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { RolloutStatus } from "routes/healthForms/api";
import { queryKeys } from "utils/misc";

export type EnvGroup = "stage" | "prod";

export type ScheduleGroupRollout = {
  group: EnvGroup;
  status: RolloutStatus;
  has_undeployed_changes: boolean;
  deployed_at: string | null;
  deployed_by_email: string | null;
  confirmed_at: string | null;
};

export type ScheduleRollout = {
  schedule_key: string;
  groups: ScheduleGroupRollout[];
};

const ROLLOUT_POLL_INTERVAL = 30_000;

export const scheduleRolloutsKey = ["schedule-rollouts"] as const;

const hasPendingRollout = (rollouts?: ScheduleRollout[]) =>
  rollouts?.some(rollout => rollout.groups.some(group => group.status === "activating")) ?? false;

export const useGetScheduleRollouts = () =>
  useQuery<ScheduleRollout[]>(
    scheduleRolloutsKey,
    async () => {
      // @ts-ignore — v2 endpoint, not in OpenAPI types
      const data = await api.parse("GET /treatments/schedules/deployments", { root: supportApiBase });
      return Array.isArray(data) ? data : [];
    },
    {
      retry: false,
      refetchInterval: data => (hasPendingRollout(data) ? ROLLOUT_POLL_INTERVAL : false),
    }
  );

export const useDeploySchedule = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ scheduleId, group }: { scheduleId: number; group: EnvGroup }): Promise<void> => {
      await api.call(`/treatments/schedules/${scheduleId}/deployments`, {
        root: supportApiBase,
        method: "POST",
        data: { group },
      });
    },
    {
      onSuccess: (_result, { scheduleId, group }) => {
        queryClient.invalidateQueries(scheduleRolloutsKey);
        queryClient.invalidateQueries(deployedScheduleKey(scheduleId, group));
      },
    }
  );
};

export type DeployedSchedule = {
  key: string;
  name: Record<string, string> | null;
  exercises_per_day: number;
  includes_functional_test: boolean;
  health_form_type: string;
  duration_weeks: number | null;
  group_key: string | null;
  exercises: {
    exercise_name: string;
    days: number[] | null;
    weeks: number[] | null;
    recommended_level: number;
    recommended_sets: number | null;
    recommended_reps: string | null;
    level_locked: boolean;
    pinned: boolean;
  }[];
  lessons: { lesson_name: string; position: number }[];
  sustain_exercises: { exercise_name: string }[];
};

export const deployedScheduleKey = (scheduleId: number, group: EnvGroup) =>
  ["schedule-deployed", scheduleId, group] as const;

export const deployableScheduleKey = (scheduleId: number) => ["schedule-deployable", scheduleId] as const;

const errorStatus = (error: unknown): number | undefined => {
  try {
    return (JSON.parse((error as Error).message) as APIError).status;
  } catch (err) {
    return undefined;
  }
};

export const useGetDeployedSchedule = (scheduleId: number | null, group: EnvGroup, enabled: boolean) =>
  useQuery<DeployedSchedule | null>(
    deployedScheduleKey(scheduleId ?? 0, group),
    async () => {
      try {
        // @ts-ignore — v2 endpoint, not in OpenAPI types
        return await api.parse(`GET /treatments/schedules/${scheduleId}/deployments/${group}`, {
          root: supportApiBase,
        });
      } catch (error) {
        if (errorStatus(error) === 404) return null;
        throw error;
      }
    },
    { enabled: enabled && scheduleId != null, retry: false, refetchOnMount: "always" }
  );

export const useGetDeployableSchedule = (scheduleId: number | null, enabled: boolean) =>
  useQuery<DeployedSchedule | null>(
    deployableScheduleKey(scheduleId ?? 0),
    async () => {
      // @ts-ignore — v2 endpoint, not in OpenAPI types
      return api.parse(`GET /treatments/schedules/${scheduleId}/deployable`, { root: supportApiBase });
    },
    { enabled: enabled && scheduleId != null, retry: false, refetchOnMount: "always" }
  );

export const useRevertToDeployed = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ scheduleId, group }: { scheduleId: number; group: EnvGroup }): Promise<void> => {
      await api.call(`/treatments/schedules/${scheduleId}/deployments/${group}/revert`, {
        root: supportApiBase,
        method: "POST",
      });
    },
    {
      onSuccess: (_result, { scheduleId, group }) => {
        queryClient.invalidateQueries(scheduleRolloutsKey);
        queryClient.invalidateQueries(queryKeys.templates());
        queryClient.invalidateQueries(deployableScheduleKey(scheduleId));
        queryClient.invalidateQueries(deployedScheduleKey(scheduleId, group));
      },
    }
  );
};
